import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import PagesAdmin from './PagesAdmin.jsx';
import ActionLandingSettings from '@/components/actions/ActionLandingSettings';
import { api } from '@/api';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';

// This wrapper enforces the two-page (register/code) model for an action.
// It auto-creates missing pages with default templates and then renders a simplified UI
// that switches between the two underlying pages but hides generic page creation.
export default function ActionDesign(){
  const { id } = useParams(); // action id
  const { token } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pages, setPages] = useState({ register:null, code:null });
  const [active, setActive] = useState('register');
  const [isMobile, setIsMobile] = useState(false);
  const [pageMeta, setPageMeta] = useState({ title:'', slug:'', dirty:false });
  // Copy/Clone Feature entfernt; kein spezielles Reloading mehr nötig.

  // Check URL hash for initial tab
  useEffect(() => {
    if (window.location.hash === '#landing') {
      setActive('landing');
    }
  }, []);

  useEffect(()=>{
    if(!token || !id) return;
    (async ()=>{
      setLoading(true); setError(null);
      try {
        // Backend endpoint to ensure both pages? We'll reuse ensureActionLandingPage twice with mode param if available.
        // For now call custom endpoints: /actions/:id/landing-page?kind=request & kind=display (heuristic by slug) or fallback.
        const reg = await api.ensureActionLandingPage(token, id); // request page
        const code = await api.ensureActionDisplayPage(token, id); // display page
        setPages({ register: reg.page || reg, code: code.page || code });
      } catch(e){ setError(e.message); }
      finally { setLoading(false); }
    })();
  },[token,id]);

  useEffect(()=>{
    function detectMobile(){
      try{
        const touch = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
        const small = window.innerWidth < 860; // editor needs horizontal space
        setIsMobile(touch && small);
      }catch{ setIsMobile(false); }
    }
    detectMobile();
    window.addEventListener('resize', detectMobile);
    return ()=> window.removeEventListener('resize', detectMobile);
  },[]);

  if(loading) return <div className='p-6 text-sm'>{t('actions.design.loadingEnv')}</div>;
  if(isMobile) return (
    <div className='p-6 text-sm'>
      <div className='max-w-md p-4 border rounded bg-amber-50 text-amber-900'>
        {t('actions.design.mobileNotAvailable')}
      </div>
    </div>
  );
  if(error) return <div className='p-6 text-sm text-red-600'>{t('actions.design.error', { error })}</div>;
  if(!pages.register || !pages.code) return <div className='p-6 text-sm'>{t('actions.design.prepareFailed')}</div>;

  // We reuse PagesAdmin but hide its left page list via CSS override and drive selection via query param.
  const pageId = active==='register' ? (pages.register.id || pages.register.pageId) : (pages.code.id || pages.code.pageId);

  const activePage = active==='register' ? pages.register : pages.code;
  return (
    <div className='flex flex-col h-[calc(100vh-60px)]'>
      <div className='border-b px-3 py-2 flex items-center gap-4 bg-background'>
        <h2 className='font-semibold text-sm flex items-center gap-3'>
          <span>{t('actions.design.title')}</span>
          {(pageMeta.title || activePage?.title) && (
            <span className='flex items-center gap-1'>
              <span className='text-slate-900'>{pageMeta.title || activePage?.title}</span>
              {(pageMeta.slug || activePage?.slug) && <span className='text-[11px] text-slate-500'>({t('actions.design.slug')}: {pageMeta.slug || activePage?.slug})</span>}
              {pageMeta.dirty && <span className='text-[10px] px-2 py-[2px] rounded bg-amber-100 text-amber-700 border border-amber-300'>{t('actions.design.unsaved')}</span>}
            </span>
          )}
        </h2>
        <div className='flex gap-2 text-[11px]'>
          <button onClick={()=>setActive('register')} className={`px-3 py-1 rounded border ${active==='register'? 'bg-blue-600 text-white border-blue-600':'bg-background'}`}>{t('actions.design.registerTab')}</button>
          <button onClick={()=>setActive('code')} className={`px-3 py-1 rounded border ${active==='code'? 'bg-blue-600 text-white border-blue-600':'bg-background'}`}>{t('actions.design.codeTab')}</button>
          <button onClick={()=>setActive('landing')} className={`px-3 py-1 rounded border ${active==='landing'? 'bg-blue-600 text-white border-blue-600':'bg-background'}`}>Landingpage</button>
        </div>
        <div className='flex-1' />
        {/* Button 'Design aus Aktion kopieren' entfernt */}
      </div>
      <div className='flex-1 overflow-hidden relative'>
        {active === 'landing' ? (
          <div className='h-full overflow-y-auto p-6 bg-muted'>
            <div className='max-w-4xl mx-auto'>
              <ActionLandingSettings actionId={id} />
            </div>
          </div>
        ) : (
          <PagesAdmin forcedPageId={pageId} modeRestricted={active} hideTheme hideTreeToggle compactHeader onMetaChange={setPageMeta} />
        )}
      </div>

  {/* Copy/Clone Modal war hier – vollständig entfernt */}
    </div>
  );
}
